"use client";

import { cn, toast, Typography } from "@kyan/ui";
import { toBlob } from "html-to-image";
import Image from "next/image";
import React, { useRef, useState } from "react";

import { type PnlCardProps } from "#/types/pnlCard";
import { formatInstrumentName } from "#/utils/instrument.utils";
import { formatCurrency } from "#/utils/valueFormatters";

import { PnLCardGeneratorActions } from "./PnLCardGeneratorActions";
import { PnLCardGeneratorSkinSelector } from "./PnLCardGeneratorSkinSelector";

const skinsStyles: Record<string, string> = {
  "pepe-cool": "w-[194px] h-[155px] right-5 top-5",
  "cat-cool": "w-75 h-62 right-0 -top-1.25 bottom-0",
  "cat-sad": "size-50 right-6 -top-1.25",
  "pepe-sad": "bottom-0 right-0 top-6 right-5",
};

export function PnLCardGenerator({
  instrumentName,
  side,
  entryPrice,
  markPrice,
  pnlAbs = 0,
  pnlPct,
}: PnlCardProps) {
  const [selectedSkin, setSelectedSkin] = useState<string | null>(null);

  const ref = useRef<HTMLDivElement>(null);

  const isPositive = pnlAbs >= 0;

  const exportBlob = async () =>
    ref.current
      ? toBlob(ref.current, {
          cacheBust: true,
          pixelRatio: 2,
          style: { transform: "scale(1)", transformOrigin: "top left" },
          filter: (node: HTMLElement) => !node.classList?.contains("no-export"),
        }).catch(() => null)
      : null;

  const copyImage = async (blob?: Blob) => {
    try {
      const imageBlob = blob ?? (await exportBlob());
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": imageBlob! }),
      ]);
      toast.success({ title: "Image has been copied to clipboard" });
    } catch (error) {
      console.error(error);
      toast.error({ title: "Failed to copy image" });
    }
  };

  const download = async () => {
    try {
      const blob = await exportBlob();
      const url = URL.createObjectURL(blob!);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${instrumentName}-${Date.now()}.png`;
      a.click();
      URL.revokeObjectURL(url);

      await copyImage(blob!);
    } catch (error) {
      console.error(error);
      toast.error({ title: "Failed to download image" });
    }
  };

  const shareOnX = async () => {
    try {
      const blob = await exportBlob();
      await copyImage(blob!);
      const url = `https://x.com/intent/post?text=${encodeURIComponent(`This is me winning:`)}`;
      window.open(url, "_blank");
    } catch (error) {
      console.error(error);
      toast.error({ title: "Failed to share on X" });
    }
  };

  return (
    <div className="mx-auto w-full pt-4">
      <div
        ref={ref}
        className="dark:border-divider relative h-67.5 overflow-hidden rounded-xl border bg-size-[100%_100%] bg-no-repeat"
        style={{
          backgroundImage: "url(/images/pnl-card/card-bg.png)",
        }}
      >
        <div className="flex items-center justify-between gap-4 px-6.5 py-3.5">
          <div className="text-lg font-semibold tracking-tight">
            <Image
              width={38}
              height={17}
              alt="KYAN logo"
              src="/images/components/logo-white.svg"
            />
          </div>
        </div>

        <div className="relative mt-5 flex items-end gap-6 px-6.5 py-3.5 text-sm">
          <div className="flex flex-col gap-5">
            <div className="flex items-center gap-2 text-white">
              <span
                className={cn("uppercase", {
                  "text-[#69FFCA]": side === "long",
                  "text-error": side === "short",
                })}
              >
                {side}
              </span>
              <span>|</span>
              {instrumentName && (
                <span className="white">
                  {formatInstrumentName(instrumentName)}
                </span>
              )}
            </div>

            <div
              className={cn("text-5xl font-extrabold", {
                "text-[#69FFCA]": isPositive,
                "text-error": !isPositive,
              })}
            >
              <span className="text-3xl">{isPositive ? "▲" : "▼"}</span>
              {pnlPct?.toFixed(3)}%
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Typography size={12} className="text-white/60 opacity-75">
                  Entry Price
                </Typography>
                <div className="text-xs tracking-tight text-white">
                  <Typography size={12} className="text-white">
                    {formatCurrency(entryPrice)}
                  </Typography>
                </div>
              </div>
              {markPrice && (
                <div className="flex gap-3">
                  <Typography size={12} className="text-white/60 opacity-75">
                    Last Price
                  </Typography>
                  <Typography size={12} className="tracking-tight text-white">
                    {formatCurrency(markPrice)}
                  </Typography>
                </div>
              )}
            </div>
          </div>
          {selectedSkin && (
            <Image
              width={200}
              height={200}
              alt={selectedSkin}
              src={`/images/pnl-card/${selectedSkin}.svg`}
              className={`absolute ${skinsStyles[selectedSkin]}`}
            />
          )}
          {!selectedSkin && (
            <Image
              width={200}
              height={200}
              alt="Diamonds"
              src="/images/pnl-card/diamonds.svg"
              className="absolute -top-11 right-0 bottom-0"
            />
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-col items-center justify-center gap-3">
        <PnLCardGeneratorSkinSelector
          selectedSkin={selectedSkin}
          setSelectedSkin={setSelectedSkin}
        />

        <PnLCardGeneratorActions
          download={download}
          shareOnX={shareOnX}
          copyImage={copyImage}
        />
      </div>
    </div>
  );
}
